import React, { FC, useCallback, useState } from 'react'
import moment from 'moment'
import { Select } from 'transfix-ui/components'
import { Card } from 'transfix-ui/components/Card'
import { Table, TableBody, TableHead, TableRow, TableCell } from 'transfix-ui/components/Table'
import { Grid, Typography } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Skeleton from 'transfix-ui/core/Skeleton'
import { makeStyles } from 'transfix-ui/core/styles'
import { theme } from 'transfix-ui'
import FORMATS from 'constants/datetime'
import { useReferenceDimensionsQuery } from './graphql/ReferenceDimensions'

const useStyles = makeStyles(() => ({
  bold: {
    fontWeight: 600,
  },
  dropdown: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5),
  },
}))

const PU_MARKET_TYPE_OPTIONS = [
  {
    label: 'Drop',
    value: 'Drop',
  },
  {
    label: 'Live',
    value: 'Live',
  },
]

interface IPuMarkets {
  puMarketType: string
  marketList: string[]
}

type TMarketType = 'Drop' | 'Live'

const rangeDesc = (start: number | null, end: number | null) => {
  return `${start ?? '-'}${start !== null && end !== null ? ' - ' : ''}${end ?? '+'}`
}

const dropMarkets = (markets: IMarket[] | undefined | null) => {
  const dropMarkets: string[] = []

  if (markets) {
    markets.forEach((market: IMarket) => {
      if (market && market.market && market.isDrop) {
        dropMarkets.push(market.market.cityState)
      }
      return null
    })
  }

  return dropMarkets
}

interface IMarket {
  market?: {
    cityState: string
  } | null
  isDrop?: boolean | null
}

const liveMarkets = (markets: IMarket[] | undefined | null) => {
  const liveMarkets: string[] = []

  if (markets) {
    markets.forEach((market: IMarket) => {
      if (market && market.market && !market.isDrop) {
        liveMarkets.push(market.market.cityState)
      }
      return null
    })
  }

  return liveMarkets
}

export const StrategyReferenceTab: FC = () => {
  const classes = useStyles()

  const {
    data: referenceDimensionsData,
    loading: referenceDimensionsDataLoading,
  } = useReferenceDimensionsQuery()

  const { referenceDimensions } = referenceDimensionsData || {}

  const pickupBetMarketsUpdatedAt = referenceDimensions?.pickupBetMarkets
    ? moment.max(referenceDimensions?.pickupBetMarkets?.map(market => moment(market.createdAt)))
    : null

  const pricingDistMarketCenterThresholdsUpdatedAt = referenceDimensions?.pricingDistMarketCenterThresholds
    ? moment(referenceDimensions?.pricingDistMarketCenterThresholds[0].updatedAt)
    : null

  const pricingVolumeGroupsUpdatedAt = referenceDimensions?.pricingVolumeGroups
    ? moment.max(referenceDimensions?.pricingVolumeGroups?.map(group => moment(group.updatedAt)))
    : null

  const pricingLengthOfHaulsUpdatedAt = referenceDimensions?.pricingLengthOfHauls
    ? moment.max(referenceDimensions?.pricingLengthOfHauls?.map(haul => moment(haul.updatedAt)))
    : null

  const [puMarketType, setPuMarketType] = useState<TMarketType>('Drop')

  const puMarketTypeChangeHandler = useCallback(({ target }) => {
    setPuMarketType(target.value)
  }, [])

  const puMarkets: IPuMarkets[] = [
    {
      puMarketType: 'Drop',
      marketList: dropMarkets(referenceDimensions?.pickupBetMarkets),
    },
    {
      puMarketType: 'Live',
      marketList: liveMarkets(referenceDimensions?.pickupBetMarkets),
    },
  ]

  return (
    <Box px={3} py={2}>
      <Box mb={2}>
        <Typography variant='h3' color='textPrimary' display='block'>
          Reference Data
        </Typography>
      </Box>
      <Box>
        <Grid container spacing={1} direction='row' alignItems='stretch'>
          <Grid item xl={6}>
            <Card>
              <Typography
                className={classes.bold}
                variant='body1'
                color='textPrimary'
                display='block'>
                List of Top PU Markets
              </Typography>
              <Typography className='mb2' variant='subtitle2' color='textSecondary'>
                Last updated{' '}
                {pickupBetMarketsUpdatedAt
                  ? pickupBetMarketsUpdatedAt.format(FORMATS.dateFormat)
                  : '-'}
              </Typography>
              <Select
                label='Type'
                options={PU_MARKET_TYPE_OPTIONS}
                value={puMarketType}
                onChange={puMarketTypeChangeHandler}
              />
              {puMarkets.map(puMarketList => {
                if (puMarketList.puMarketType === puMarketType) {
                  return puMarketList.marketList.map((market, index) => {
                    return (
                      <Typography className={index === 0 ? 'mt2' : ''}>
                        {index + 1}. {market}
                      </Typography>
                    )
                  })
                }
                return null
              })}
            </Card>
          </Grid>
          <Grid item xl={6}>
            <Card>
              <Grid container spacing={1} direction='row' alignItems='stretch'>
                <Grid item xs={6}>
                  <Typography
                    className={`fl ${classes.bold}`}
                    variant='body1'
                    color='textPrimary'
                    display='block'>
                    Distance from Market Center
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className='fr' variant='subtitle2' color='textSecondary'>
                    Last updated{' '}
                    {pricingDistMarketCenterThresholdsUpdatedAt
                      ? pricingDistMarketCenterThresholdsUpdatedAt.format(FORMATS.dateFormat)
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className='tl'>Location</TableCell>
                    <TableCell className='tl'>Threshold Distance</TableCell>
                  </TableRow>
                </TableHead>
                {referenceDimensionsDataLoading ? (
                  <TableBody>
                    <TableRow>
                      <TableCell className='tl'>
                        <Skeleton width={180} height={20} />
                      </TableCell>
                      <TableCell className='tl'>
                        <Skeleton width={180} height={20} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell className='tl'>Pickup</TableCell>
                      <TableCell className='tl'>
                        {referenceDimensions?.pricingDistMarketCenterThresholds
                          ? referenceDimensions?.pricingDistMarketCenterThresholds[0].pickup
                          : '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='tl'>Delivery</TableCell>
                      <TableCell className='tl'>
                        {referenceDimensions?.pricingDistMarketCenterThresholds
                          ? referenceDimensions?.pricingDistMarketCenterThresholds[0].delivery
                          : '-'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </Card>
            <Card className='mt2'>
              <Grid container spacing={1} direction='row' alignItems='stretch'>
                <Grid item xs={6}>
                  <Typography
                    className={`fl ${classes.bold}`}
                    variant='body1'
                    color='textPrimary'
                    display='block'>
                    Volume Group Definition
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className='fr' variant='subtitle2' color='textSecondary'>
                    Last updated{' '}
                    {pricingVolumeGroupsUpdatedAt
                      ? pricingVolumeGroupsUpdatedAt.format(FORMATS.dateFormat)
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className='tl'>Volume Group</TableCell>
                    <TableCell className='tl'>Volume</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referenceDimensionsDataLoading ? (
                    <TableRow>
                      <TableCell className='tl'>
                        <Skeleton width={180} height={20} />
                      </TableCell>
                      <TableCell className='tl'>
                        <Skeleton width={180} height={20} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {referenceDimensions?.pricingVolumeGroups?.map(group => {
                        return (
                          <TableRow>
                            <TableCell className='tl'>{group.name}</TableCell>
                            <TableCell className='tl'>
                              {`${rangeDesc(group.groupStart, group.groupEnd)} loads`}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </>
                  )}
                </TableBody>
              </Table>
            </Card>
            <Card className='mt2'>
              <Grid container spacing={1} direction='row' alignItems='stretch'>
                <Grid item xs={6}>
                  <Typography
                    className={`fl ${classes.bold}`}
                    variant='body1'
                    color='textPrimary'
                    display='block'>
                    Length of Haul Definition
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className='fr' variant='subtitle2' color='textSecondary'>
                    Last updated{' '}
                    {pricingLengthOfHaulsUpdatedAt
                      ? pricingLengthOfHaulsUpdatedAt.format(FORMATS.dateFormat)
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className='tl'>Group</TableCell>
                    <TableCell className='tl'>Miles</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referenceDimensionsDataLoading ? (
                    <TableRow>
                      <TableCell className='tl'>
                        <Skeleton width={180} height={20} />
                      </TableCell>
                      <TableCell className='tl'>
                        <Skeleton width={180} height={20} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {referenceDimensions?.pricingLengthOfHauls?.map(haul => {
                        return (
                          <TableRow>
                            <TableCell className='tl'>{haul.name}</TableCell>
                            <TableCell className='tl'>
                              {`${rangeDesc(haul.lengthStart, haul.lengthEnd)} Miles`}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </>
                  )}
                </TableBody>
              </Table>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
